import { useState, useEffect, useCallback, useMemo } from 'react';
import AppleLogo from 'Shared/icons/logos/apple.svg';
import { isAndroidMobileApp, loadAppleSdk } from 'Shared/appleHelper';
import { useTranslations } from '@ifixit/i18n/hooks';

export function AppleSSOButton({
   loginHandler,
   setError,
   text,
}: {
   loginHandler: (response: any) => void;
   setError: (error: string) => void;
   text: string;
}) {
   const t = useTranslations('Login');
   const [isLoading, setIsLoading] = useState(false);
   const [isReady, setIsReady] = useState(false);
   const userAgent = navigator.userAgent || '';
   const onAndroidMobileApp = useMemo(() => isAndroidMobileApp(userAgent), [userAgent]);

   useEffect(() => {
      if (!App.appleLoginEnabled || !App.appleClientId) {
         return;
      }

      loadAppleSdk(() => setIsReady(true));
   }, []);

   const handleClick = useCallback(async () => {
      if (!isReady || isLoading) return;

      setIsLoading(true);
      try {
         const response = await window.AppleID.auth.signIn();

         // Send the identity token to our backend
         const apiResponse = await fetch('/api/2.0/user/login/appleLogin', {
            method: 'POST',
            headers: {
               'Content-Type': 'application/json',
            },
            body: JSON.stringify({
               identityToken: response.authorization.id_token,
               user: response.user,
            }),
         });

         const result = await apiResponse.json();

         if (result.loggedIn) {
            result.from = 'apple';
            loginHandler(result);
         } else {
            setError(result.reason || t('unableToCompleteAppleLogin'));
         }
      } catch (error: any) {
         // User cancelled or other error
         if (error?.error !== 'popup_closed_by_user') {
            setError(t('unableToCompleteAppleLogin'));
         }
      } finally {
         setIsLoading(false);
      }
   }, [isReady, isLoading, loginHandler, setError, t]);

   if (!App.appleLoginEnabled || onAndroidMobileApp) {
      return null;
   }

   return (
      <button
         id="appleLogin"
         className="button-outline button-lg w-full gap-2"
         onClick={handleClick}
         disabled={!isReady}
         data-mobile-appid="appleLogin"
      >
         <AppleLogo className="size-6.5" />
         {text}
      </button>
   );
}
